<template>
  <div class="usage-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h2>{{ $t('usageManagement.title') }}</h2>
        <div class="header-actions">
          <a-space>
            <a-button type="primary" @click="showQuotaAllocationModal">
              <template #icon>
                <PlusOutlined />
              </template>
              {{ $t('usageManagement.allocateQuota') }}
            </a-button>
            <a-button @click="refreshData">
              <template #icon>
                <ReloadOutlined />
              </template>
              {{ $t('usageManagement.refresh') }}
            </a-button>
          </a-space>
        </div>
      </div>
    </div>

    <!-- 统计卡片 -->
    <a-row :gutter="16" class="stats-row">
      <a-col :span="6">
        <a-card>
          <a-statistic
            :title="$t('usageManagement.totalGroups')"
            :value="summary.total_groups"
          />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            :title="$t('usageManagement.activeQuotas')"
            :value="summary.active_quotas"
          />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            :title="$t('usageManagement.usageAlertsCount')"
            :value="summary.usage_alerts?.length || 0"
          />
        </a-card>
      </a-col>
      <a-col :span="6">
        <a-card>
          <a-statistic
            :title="$t('usageManagement.negativeUsageUsers')"
            :value="summary.negative_usage_users?.length || 0"
          />
        </a-card>
      </a-col>
    </a-row>

    <!-- 用量预警监控面板 -->
    <a-row :gutter="16" class="stats-row">
        <a-col :span="12">
          <a-card :title="$t('usageManagement.characterUsageAlert')" size="small" :class="{ 'alert-card': summary.character_alerts?.length > 0 }">
            <template #extra>
              <a-tag :color="summary.character_alerts?.length > 0 ? 'red' : 'green'">
                {{ summary.character_alerts?.length || 0 }} {{ $t('usageManagement.alerts') }}
              </a-tag>
            </template>
            <div v-if="summary.character_alerts?.length > 0">
              <div v-for="alert in summary.character_alerts.slice(0, 3)" :key="alert.user_id" class="alert-item">
                <a-tag color="red">{{ alert.group_name }}</a-tag>
                <span class="alert-text">{{ $t('usageManagement.remaining') }}: {{ alert.remaining_quota }} {{ $t('usageManagement.characters') }}</span>
                <span class="alert-threshold">{{ $t('usageManagement.usageRate') }}: {{ alert.usage_rate?.toFixed(1) || '0.0' }}%</span>
              </div>
              <div v-if="summary.character_alerts.length > 3" class="more-alerts">
                {{ $t('usageManagement.moreAlerts', { count: summary.character_alerts.length - 3 }) }}
              </div>
            </div>
            <a-empty v-else :description="$t('usageManagement.noCharacterUsageAlert')" />
          </a-card>
        </a-col>
        <a-col :span="12">
          <a-card :title="$t('usageManagement.timeUsageAlert')" size="small" :class="{ 'alert-card': summary.time_alerts?.length > 0 }">
            <template #extra>
              <a-tag :color="summary.time_alerts?.length > 0 ? 'red' : 'green'">
                {{ summary.time_alerts?.length || 0 }} {{ $t('usageManagement.alerts') }}
              </a-tag>
            </template>
            <div v-if="summary.time_alerts?.length > 0">
              <div v-for="alert in summary.time_alerts.slice(0, 3)" :key="alert.user_id" class="alert-item">
                <a-tag color="red">{{ alert.group_name }}</a-tag>
                <span class="alert-text">{{ $t('usageManagement.remaining') }}: {{ Math.round(alert.remaining_quota / 60) }} {{ $t('common.minutes') }}</span>
                <span class="alert-threshold">{{ $t('usageManagement.usageRate') }}: {{ alert.usage_rate?.toFixed(1) || '0.0' }}%</span>
              </div>
              <div v-if="summary.time_alerts.length > 3" class="more-alerts">
                {{ $t('usageManagement.moreAlerts', { count: summary.time_alerts.length - 3 }) }}
              </div>
            </div>
            <a-empty v-else :description="$t('usageManagement.noTimeUsageAlert')" />
          </a-card>
      </a-col>
    </a-row>

    <!-- 负用量用户管理 -->
    <a-card v-if="summary.negative_usage_users?.length > 0" :title="$t('usageManagement.negativeUsageUsersManagement')" class="mb-4" type="warning">
      <template #extra>
        <a-button type="primary" size="small" @click="refreshNegativeUsageData">
          <ReloadOutlined />
          {{ $t('usageManagement.refreshStatus') }}
        </a-button>
      </template>
      <a-table
        :columns="negativeUsageColumns"
        :data-source="summary.negative_usage_users"
        :pagination="{ pageSize: 5, hideOnSinglePage: true }"
        size="small"
      >
        <template #bodyCell="{ column, record }">
          <template v-if="column.key === 'group_name'">
            <a-tag color="red">{{ record.group_name }}</a-tag>
          </template>
          <template v-else-if="column.key === 'usage_status'">
            <a-tag color="error">{{ $t('usageManagement.apiCallBlocked') }}</a-tag>
          </template>
          <template v-else-if="column.key === 'remaining_quota'">
            <span class="negative-amount">{{ record.remaining_quota }}</span>
          </template>
          <template v-else-if="column.key === 'action'">
            <a-button type="link" size="small" @click="allocateEmergencyQuota(record)">
              {{ $t('usageManagement.emergencyQuota') }}
            </a-button>
          </template>
        </template>
      </a-table>
    </a-card>

    <!-- 用量不足警告 -->
    <a-alert
      v-if="summary.low_quota_alerts && summary.low_quota_alerts.length > 0"
      :message="$t('usageManagement.lowQuotaWarning')"
      type="warning"
      show-icon
      class="mb-4"
    >
      <template #description>
        <div v-for="alert in summary.low_quota_alerts" :key="`${alert.group_id}-${alert.usage_type}`">
          {{ $t('usageManagement.lowQuotaAlertMessage', { 
            groupName: alert.group_name, 
            usageType: alert.usage_type === 'character' ? $t('usageManagement.characterUsage') : $t('usageManagement.timeUsage'),
            usageRate: alert.usage_rate.toFixed(1)
          }) }}
        </div>
      </template>
    </a-alert>

    <!-- 用量表格 -->
    <a-card>
      <a-tabs v-model:activeKey="activeTab">
        <a-tab-pane key="quotas" :tab="$t('usageManagement.quotaManagement')">
          <a-table
            :columns="quotaColumns"
            :data-source="quotaList"
            :loading="loading"
            row-key="id"
            :pagination="quotaPagination"
            @change="handleQuotaTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'group_name'">
                <a-tag color="blue">{{ record.group_name }}</a-tag>
              </template>
              <template v-else-if="column.key === 'usage_type'">
                <a-tag :color="record.usage_type === 'character' ? 'green' : 'orange'">
                  {{ record.usage_type === 'character' ? $t('usageManagement.characterType') : $t('usageManagement.timeType') }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'total_quota'">
                {{ formatNumberWithLocale(record.total_quota) }}
              </template>
              <template v-else-if="column.key === 'used_quota'">
                {{ formatNumberWithLocale(record.used_quota) }}
              </template>
              <template v-else-if="column.key === 'remaining_quota'">
                {{ formatNumberWithLocale(record.remaining_quota) }}
              </template>
              <template v-else-if="column.key === 'usage_rate'">
                {{ record.usage_rate?.toFixed(2) || '0.00' }}%
              </template>
              <template v-else-if="column.key === 'status'">
                <a-tag :color="record.is_active ? 'success' : 'default'">
                  {{ record.is_active ? $t('usageManagement.activated') : $t('usageManagement.deactivated') }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'actions'">
                <a-space>
                  <a-button type="link" size="small" @click="editQuota(record)">
                    {{ $t('usageManagement.edit') }}
                  </a-button>
                  <a-button type="link" size="small" @click="resetQuota(record)">
                    {{ $t('usageManagement.resetQuota') }}
                  </a-button>
                  <a-button 
                    type="link" 
                    size="small" 
                    :danger="record.is_active"
                    @click="toggleQuotaStatus(record)"
                  >
                    {{ record.is_active ? $t('usageManagement.deactivate') : $t('usageManagement.activate') }}
                  </a-button>
                </a-space>
              </template>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="records" :tab="$t('usageManagement.usageRecords')">
          <div class="mb-3">
            <a-form layout="inline" :model="recordFilter">
              <a-form-item :label="$t('usageManagement.userGroup')">
                <a-select
                  v-model:value="recordFilter.group_id"
                  :placeholder="$t('usageManagement.selectUserGroup')"
                  style="width: 150px"
                  allow-clear
                >
                  <a-select-option v-for="group in groupOptions" :key="group.id" :value="group.id">
                    {{ group.name }}
                  </a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item :label="$t('usageManagement.serviceType')">
                <a-select
                  v-model:value="recordFilter.service_type"
                  :placeholder="$t('usageManagement.selectServiceType')"
                  style="width: 150px"
                  allow-clear
                >
                  <a-select-option value="video_translation">{{ $t('usage.videoTranslation') }}</a-select-option>
                  <a-select-option value="document_translation">{{ $t('usage.documentTranslation') }}</a-select-option>
                  <a-select-option value="message_translation">{{ $t('usage.messageTranslation') }}</a-select-option>
                  <a-select-option value="hot_word_extraction">{{ $t('usage.hotWordExtraction') }}</a-select-option>
                  <a-select-option value="hot_word_translation">{{ $t('usage.hotWordTranslation') }}</a-select-option>
                  <a-select-option value="speech_to_text">{{ $t('usage.speechToText') }}</a-select-option>
                  <a-select-option value="speech_translation">{{ $t('usage.speechTranslation') }}</a-select-option>
                  <a-select-option value="ai_chat">{{ $t('usage.aiChat') }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item :label="$t('usageManagement.usageType')">
                <a-select
                  v-model:value="recordFilter.usage_type"
                  :placeholder="$t('usageManagement.selectUsageType')"
                  style="width: 120px"
                  allow-clear
                >
                  <a-select-option value="character">{{ $t('usageManagement.characterType') }}</a-select-option>
                  <a-select-option value="time">{{ $t('usageManagement.timeType') }}</a-select-option>
                </a-select>
              </a-form-item>
              <a-form-item :label="$t('usageManagement.dateRange')">
                <a-range-picker
                  v-model:value="recordFilter.dateRange"
                  style="width: 240px"
                  :placeholder="[$t('common.startDate'), $t('common.endDate')]"
                  @change="onRecordDateRangeChange"
                />
              </a-form-item>
              <a-form-item>
                <a-button type="primary" @click="searchRecords">
                  {{ $t('common.search') }}
                </a-button>
                <a-button @click="resetDateRange" class="ml-2">
                  {{ $t('usageManagement.resetDateRange') }}
                </a-button>
              </a-form-item>
            </a-form>
          </div>

          <a-table
            :columns="recordColumns"
            :data-source="recordList"
            :loading="recordLoading"
            row-key="id"
            :pagination="recordPagination"
            @change="handleRecordTableChange"
          >
            <template #bodyCell="{ column, record }">
              <template v-if="column.key === 'user_name'">
                <a-tag color="blue">{{ record.user_name || $t('usageManagement.unknownUser') }}</a-tag>
              </template>
              <template v-else-if="column.key === 'service_type'">
                <a-tag :color="getServiceTypeColorWithT(record.service_type)">
                  {{ getServiceTypeNameWithT(record.service_type) }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'usage_type'">
                <a-tag :color="record.usage_type === 'character' ? 'green' : 'orange'">
                  {{ record.usage_type === 'character' ? $t('usageManagement.characterType') : $t('usageManagement.timeType') }}
                </a-tag>
              </template>
              <template v-else-if="column.key === 'amount'">
                {{ formatNumberWithLocale(record.amount) }}
                {{ record.usage_type === 'character' ? $t('usageManagement.characters') : $t('common.seconds') }}
              </template>
              <template v-else-if="column.key === 'created_at'">
                {{ formatDateTimeWithLocale(record.created_at) }}
              </template>
            </template>
          </a-table>
        </a-tab-pane>

        <a-tab-pane key="statistics" :tab="$t('usageManagement.statisticalReports')">
          <usage-statistics-chart :group-id="selectedGroupForStats" />
        </a-tab-pane>
      </a-tabs>
    </a-card>

    <!-- 用量分配模态框 -->
    <a-modal
      v-model:open="quotaAllocationVisible"
      :title="$t('usageManagement.allocationTitle')"
      width="600px"
      @ok="handleQuotaAllocation"
      @cancel="resetQuotaAllocationForm"
    >
      <a-form
        ref="quotaAllocationFormRef"
        :model="quotaAllocationForm"
        :rules="quotaAllocationRules"
        layout="vertical"
      >
        <a-form-item :label="$t('usageManagement.userGroup')" name="group_id">
          <a-select
            v-model:value="quotaAllocationForm.group_id"
            :placeholder="$t('usageManagement.selectUserGroup')"
          >
            <a-select-option v-for="group in groupOptions" :key="group.id" :value="group.id">
              {{ group.name }}
            </a-select-option>
          </a-select>
        </a-form-item>
        <a-form-item :label="$t('usageManagement.characterQuota')" name="character_quota">
          <a-input-number
            v-model:value="quotaAllocationForm.character_quota"
            :placeholder="$t('usageManagement.enterCharacterQuota')"
            :min="0"
            :step="1000"
            style="width: 100%"
          />
          <div class="text-gray-500 text-sm mt-1">{{ $t('usageManagement.unitCharacters') }}</div>
        </a-form-item>
        <a-form-item :label="$t('usageManagement.timeQuota')" name="time_quota">
          <a-input-number
            v-model:value="quotaAllocationForm.time_quota"
            :placeholder="$t('usageManagement.enterTimeQuota')"
            :min="0"
            :step="60"
            style="width: 100%"
          />
          <div class="text-gray-500 text-sm mt-1">{{ $t('usageManagement.unitSeconds') }}</div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 用量编辑模态框 -->
    <a-modal
      v-model:open="quotaEditVisible"
      :title="$t('usageManagement.editQuota')"
      @ok="handleQuotaEdit"
      @cancel="resetQuotaEditForm"
    >
      <a-form
        ref="quotaEditFormRef"
        :model="quotaEditForm"
        :rules="quotaEditRules"
        layout="vertical"
      >
        <a-form-item :label="$t('usageManagement.addQuota')" name="additional_quota">
          <a-input-number
            v-model:value="quotaEditForm.additional_quota"
            :placeholder="$t('usageManagement.enterAdditionalQuota')"
            :min="0"
            style="width: 100%"
          />
          <div class="text-gray-500 text-sm mt-1">
            {{ $t('usageManagement.currentRemaining') }}: {{ formatNumberWithLocale(currentEditingQuota?.remaining_quota || 0) }}
            {{ currentEditingQuota?.usage_type === 'character' ? $t('usageManagement.characters') : $t('common.seconds') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>

    <!-- 用量重置模态框 -->
    <a-modal
      v-model:open="quotaResetVisible"
      :title="$t('usageManagement.resetQuotaTitle')"
      @ok="handleQuotaReset"
      @cancel="resetQuotaResetForm"
    >
      <a-form
        ref="quotaResetFormRef"
        :model="quotaResetForm"
        :rules="quotaResetRules"
        layout="vertical"
      >
        <a-alert
          :message="$t('usageManagement.resetWarning')"
          :description="$t('usageManagement.resetWarningMessage')"
          type="warning"
          show-icon
          class="mb-4"
        />
        <a-form-item :label="$t('usageManagement.newTotalQuota')" name="new_total">
          <a-input-number
            v-model:value="quotaResetForm.new_total"
            :placeholder="$t('usageManagement.enterNewTotalQuota')"
            :min="0"
            style="width: 100%"
          />
          <div class="text-gray-500 text-sm mt-1">
            {{ $t('usageManagement.currentTotalQuota') }}: {{ formatNumberWithLocale(currentEditingQuota?.total_quota || 0) }}
            {{ currentEditingQuota?.usage_type === 'character' ? $t('usageManagement.characters') : $t('common.seconds') }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed } from 'vue'
import { message } from 'ant-design-vue'
import { useI18n } from 'vue-i18n'
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons-vue'
import type { TableColumnsType, TablePaginationConfig } from 'ant-design-vue'
import { usageApi } from '@/modules/usage/api'
import { getUserGroups } from '@/modules/user/api'
import { runApiBatch, type BatchContext } from '@/shared/api/use-api-batch'
import { UsageStatisticsChart } from '@/modules/usage/components'
import dayjs from 'dayjs'

const { t } = useI18n()

// 响应式数据
const loading = ref(false)
const recordLoading = ref(false)
const activeTab = ref('quotas')

// 摘要数据
const summary = ref<any>({})

// 用量数据
const quotaList = ref<any[]>([])
const quotaPagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
})

// 用量记录数据
const recordList = ref<any[]>([])
const recordPagination = reactive({
  current: 1,
  pageSize: 10,
  total: 0,
  showSizeChanger: true,
  showQuickJumper: true,
})

// 用户组选项
const groupOptions = ref<any[]>([])

// 记录过滤器
const recordFilter = reactive({
  group_id: undefined as number | undefined,
  service_type: undefined as string | undefined,
  usage_type: undefined as string | undefined,
  dateRange: undefined as any,
})

/** 用户手动改过日期范围后，刷新时不自动推进 end_date */
const recordDateRangeCustomized = ref(false)

// 统计图表选中的组
const selectedGroupForStats = ref<number | undefined>(undefined)

// 用量分配模态框
const quotaAllocationVisible = ref(false)
const quotaAllocationFormRef = ref()
const quotaAllocationForm = reactive({
  group_id: 0,
  character_quota: 0,
  time_quota: 0,
})
const quotaAllocationRules = computed(() => ({
  group_id: [{ required: true, message: t('usageManagement.selectUserGroupRequired') }],
}))

// 用量编辑模态框
const quotaEditVisible = ref(false)
const quotaEditFormRef = ref()
const quotaEditForm = reactive({
  additional_quota: 0,
})
const quotaEditRules = computed(() => ({
  additional_quota: [{ required: true, message: t('usageManagement.enterAdditionalQuotaRequired') }],
}))
const currentEditingQuota = ref<any>(null)

// 用量重置模态框
const quotaResetVisible = ref(false)
const quotaResetFormRef = ref()
const quotaResetForm = reactive({
  new_total: 0,
})
const quotaResetRules = computed(() => ({
  new_total: [{ required: true, message: t('usageManagement.enterNewTotalQuotaRequired') }],
}))

// 负用量用户表格列定义
const negativeUsageColumns = computed<TableColumnsType>(() => [
  {
    title: t('usageManagement.userGroup'),
    key: 'group_name',
    dataIndex: 'group_name',
  },
  {
    title: t('usageManagement.usageType'),
    key: 'usage_type',
    dataIndex: 'usage_type',
  },
  {
    title: t('usageManagement.remainingQuota'),
    key: 'remaining_quota',
    dataIndex: 'remaining_quota',
  },
  {
    title: t('common.status'),
    key: 'usage_status',
  },
  {
    title: t('common.operation'),
    key: 'action',
    width: 120,
  },
])

// 表格列定义
const quotaColumns = computed<TableColumnsType>(() => [
  {
    title: t('common.serialNumber'),
    key: 'index',
    width: 60,
    customRender: ({ index }) => quotaPagination.pageSize * (quotaPagination.current - 1) + index + 1,
  },
  {
    title: t('usageManagement.userGroup'),
    key: 'group_name',
    dataIndex: 'group_name',
  },
  {
    title: t('usageManagement.usageType'),
    key: 'usage_type',
    dataIndex: 'usage_type',
  },
  {
    title: t('usageManagement.totalQuota'),
    key: 'total_quota',
    dataIndex: 'total_quota',
    width: 120,
  },
  {
    title: t('usageManagement.used'),
    key: 'used_quota',
    dataIndex: 'used_quota',
    width: 120,
  },
  {
    title: t('usageManagement.remaining'),
    key: 'remaining_quota',
    dataIndex: 'remaining_quota',
    width: 120,
  },
  {
    title: t('usageManagement.usageRate'),
    key: 'usage_rate',
    width: 120,
  },
  {
    title: t('common.status'),
    key: 'status',
    dataIndex: 'is_active',
  },
  {
    title: t('common.operation'),
    key: 'actions',
    width: 200,
  },
])

const recordColumns = computed<TableColumnsType>(() => [
  {
    title: t('common.serialNumber'),
    key: 'index',
    width: 60,
    customRender: ({ index }) => recordPagination.pageSize * (recordPagination.current - 1) + index + 1,
  },
  {
    title: t('usageManagement.user'),
    key: 'user_name',
  },
  {
    title: t('usageManagement.serviceType'),
    key: 'service_type',
    dataIndex: 'service_type',
  },
  {
    title: t('usageManagement.usageType'),
    key: 'usage_type',
    dataIndex: 'usage_type',
  },
  {
    title: t('usageManagement.consumptionAmount'),
    key: 'amount',
  },
  {
    title: t('usageManagement.apiName'),
    key: 'api_name',
    dataIndex: 'api_name',
  },
  {
    title: t('usageManagement.createTime'),
    key: 'created_at',
  },
])

// 方法
const syncDateRangeEndToNow = () => {
  if (recordDateRangeCustomized.value) {
    return
  }
  if (recordFilter.dateRange?.[0]) {
    recordFilter.dateRange = [recordFilter.dateRange[0], dayjs()]
  } else {
    setDefaultDateRange()
  }
}

const onRecordDateRangeChange = () => {
  recordDateRangeCustomized.value = true
}

const refreshData = async () => {
  syncDateRangeEndToNow()
  await runApiBatch([
    (ctx) => loadSummary(ctx),
    (ctx) => loadQuotaList(ctx),
    (ctx) => loadRecordList(ctx),
    (ctx) => loadGroupOptions(ctx),
  ])
}

const loadSummary = async (batchCtx?: BatchContext) => {
  try {
    const response = await usageApi.getManagementSummary(batchCtx?.requestConfig)
    summary.value = response || {}
  } catch (error) {
    console.error('Failed to load summary:', error)
    summary.value = {
      total_groups: 0,
      active_quotas: 0,
      low_quota_alerts: [],
      recent_usage: [],
      usage_alerts: [],
      negative_usage_users: [],
      character_alerts: [],
      time_alerts: []
    }
    throw error
  }
}

const loadQuotaList = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  if (manageLoading) {
    loading.value = true
  }
  try {
    const params = {
      skip: (quotaPagination.current - 1) * quotaPagination.pageSize,
      limit: quotaPagination.pageSize
    }

    const response = await usageApi.getAllQuotas(params, batchCtx?.requestConfig)

    if (response && typeof response === 'object' && 'data' in response) {
      quotaList.value = response.data || []
      quotaPagination.total = response.total || 0
    } else {
      quotaList.value = Array.isArray(response) ? response : []
      quotaPagination.total = quotaList.value.length
    }
  } catch (error) {
    console.error('Failed to load quota list:', error)
    quotaList.value = []
    quotaPagination.total = 0
    throw error
  } finally {
    if (manageLoading) {
      loading.value = false
    }
  }
}

const loadRecordList = async (batchCtx?: BatchContext) => {
  const manageLoading = !batchCtx
  if (manageLoading) {
    recordLoading.value = true
  }
  try {
    const params: Record<string, unknown> = {
      limit: recordPagination.pageSize,
      offset: (recordPagination.current - 1) * recordPagination.pageSize,
    }

    if (recordFilter.group_id && recordFilter.group_id > 0) {
      params.group_id = recordFilter.group_id
    }
    if (recordFilter.service_type?.trim()) {
      params.service_type = recordFilter.service_type
    }
    if (recordFilter.usage_type?.trim()) {
      params.usage_type = recordFilter.usage_type
    }

    if (recordFilter.dateRange?.[0] && recordFilter.dateRange?.[1]) {
      params.start_date = dayjs(recordFilter.dateRange[0]).toISOString()
      params.end_date = dayjs(recordFilter.dateRange[1]).toISOString()
    } else {
      const endDate = dayjs()
      const startDate = dayjs().subtract(30, 'day')
      params.start_date = startDate.toISOString()
      params.end_date = endDate.toISOString()
    }

    const response = await usageApi.getUsageRecords(
      params as Parameters<typeof usageApi.getUsageRecords>[0],
      batchCtx?.requestConfig,
    )

    if (response && typeof response === 'object' && 'items' in response) {
      recordList.value = response.items || []
      recordPagination.total = response.total || 0
    } else if (response && typeof response === 'object' && 'data' in response) {
      recordList.value = (response as { data?: unknown[] }).data || []
      recordPagination.total = (response as { total?: number }).total || 0
    } else {
      recordList.value = Array.isArray(response) ? response : []
      recordPagination.total = recordList.value.length
    }
  } catch (error) {
    console.error('Failed to load usage records:', error)
    throw error
  } finally {
    if (manageLoading) {
      recordLoading.value = false
    }
  }
}

const loadGroupOptions = async (batchCtx?: BatchContext) => {
  try {
    const response = await getUserGroups(batchCtx?.requestConfig)
    groupOptions.value = response || []
  } catch (error) {
    console.error('Failed to load user groups:', error)
    groupOptions.value = []
    throw error
  }
}

// 处理表格变化（分页、排序和筛选）
// @change 事件会传递三个参数：(pagination, filters, sorter)
const handleQuotaTableChange = (pag: any, filters: any, sorter: any) => {
  // 处理分页变化
  if (pag) {
    const newCurrent = pag.current || quotaPagination.current
    const newPageSize = pag.pageSize || quotaPagination.pageSize
    
    // 只有在页码或每页数量变化时才重新加载
    if (newCurrent !== quotaPagination.current || newPageSize !== quotaPagination.pageSize) {
      quotaPagination.current = newCurrent
      quotaPagination.pageSize = newPageSize
      loadQuotaList()
    }
  }
  
  // 处理排序和筛选（如果需要的话）
}

const handleRecordTableChange = (pag: any, filters: any, sorter: any) => {
  // 处理分页变化
  if (pag) {
    const newCurrent = pag.current || recordPagination.current
    const newPageSize = pag.pageSize || recordPagination.pageSize
    
    // 只有在页码或每页数量变化时才重新加载
    if (newCurrent !== recordPagination.current || newPageSize !== recordPagination.pageSize) {
      recordPagination.current = newCurrent
      recordPagination.pageSize = newPageSize
      loadRecordList()
    }
  }
  
  // 处理排序和筛选（如果需要的话）
}

const searchRecords = () => {
  recordPagination.current = 1
  loadRecordList()
}

const showQuotaAllocationModal = () => {
  quotaAllocationVisible.value = true
}

const handleQuotaAllocation = async () => {
  try {
    await quotaAllocationFormRef.value.validate()
  } catch {
    return
  }
  try {
    await usageApi.allocateQuotas(quotaAllocationForm)
    message.success(t('usageManagement.allocationSuccess'))
    quotaAllocationVisible.value = false
    resetQuotaAllocationForm()
    refreshData()
  } catch (error) {
    console.error('Failed to allocate quota:', error)
  }
}

const resetQuotaAllocationForm = () => {
  quotaAllocationForm.group_id = 0
  quotaAllocationForm.character_quota = 0
  quotaAllocationForm.time_quota = 0
  quotaAllocationFormRef.value?.resetFields()
}

const editQuota = (record: any) => {
  currentEditingQuota.value = record
  quotaEditVisible.value = true
}

const handleQuotaEdit = async () => {
  try {
    await quotaEditFormRef.value.validate()
  } catch {
    return
  }
  try {
    await usageApi.createOrUpdateQuota({
      group_id: currentEditingQuota.value.group_id,
      usage_type: currentEditingQuota.value.usage_type,
      total_quota: quotaEditForm.additional_quota,
    })
    message.success(t('usageManagement.editQuotaSuccess'))
    quotaEditVisible.value = false
    resetQuotaEditForm()
    refreshData()
  } catch (error) {
    console.error('Failed to edit quota:', error)
  }
}

const resetQuotaEditForm = () => {
  quotaEditForm.additional_quota = 0
  quotaEditFormRef.value?.resetFields()
  currentEditingQuota.value = null
}

const resetQuota = (record: any) => {
  currentEditingQuota.value = record
  quotaResetForm.new_total = record.total_quota
  quotaResetVisible.value = true
}

const handleQuotaReset = async () => {
  try {
    await quotaResetFormRef.value.validate()
  } catch {
    return
  }
  try {
    await usageApi.resetQuota({
      group_id: currentEditingQuota.value.group_id,
      usage_type: currentEditingQuota.value.usage_type,
      new_total: quotaResetForm.new_total,
    })
    message.success(t('usageManagement.resetSuccess'))
    quotaResetVisible.value = false
    resetQuotaResetForm()
    refreshData()
  } catch (error) {
    console.error('Failed to reset quota:', error)
  }
}

const resetQuotaResetForm = () => {
  quotaResetForm.new_total = 0
  quotaResetFormRef.value?.resetFields()
  currentEditingQuota.value = null
}

const toggleQuotaStatus = async (record: any) => {
  try {
    await usageApi.toggleQuotaStatus(record.id)
    const statusText = record.is_active ? t('usageManagement.deactivated') : t('usageManagement.activated')
    message.success(t('usageManagement.toggleQuotaStatusSuccess', { status: statusText }))
    refreshData()
  } catch (error) {
    console.error('Failed to toggle quota status:', error)
  }
}

// 新增：刷新负用量数据
const refreshNegativeUsageData = async () => {
  try {
    await loadSummary()
    message.success(t('usageManagement.refreshNegativeUsageSuccess'))
  } catch (error) {
    console.error('Failed to refresh negative usage:', error)
  }
}

// 新增：紧急用量分配
const allocateEmergencyQuota = async (_record: any) => {
  try {
    message.success(t('usageManagement.emergencyQuotaAllocationSuccess'))
    refreshData()
  } catch (error) {
    console.error('Failed to allocate emergency quota:', error)
  }
}

// 新增：重置过滤器
const resetRecordFilter = () => {
  recordFilter.group_id = undefined
  recordFilter.service_type = undefined
  recordFilter.usage_type = undefined
  recordFilter.dateRange = undefined
  recordDateRangeCustomized.value = false
}

// 工具函数
import { formatNumber, formatDateTime } from '@/shared/utils'
import { getServiceTypeColor, getServiceTypeName } from '@/modules/usage/utils'

// 使用公共工具函数
const formatNumberWithLocale = (value: number) => {
  if (value == null) return '0'
  const { locale } = useI18n()
  const numberLocale = locale.value === 'zh' ? 'zh-CN' : 'en-US'
  return formatNumber(value, 0, numberLocale)
}

const formatDateTimeWithLocale = (dateTime: string) => {
  return formatDateTime(dateTime)
}

const getServiceTypeColorWithT = (type: string) => {
  return getServiceTypeColor(type)
}

const getServiceTypeNameWithT = (type: string) => {
  return getServiceTypeName(type, t)
}

// 生命周期
onMounted(() => {
  // 确保过滤器是干净的
  resetRecordFilter()
  // 设置默认日期范围为最近30天
  setDefaultDateRange()
  refreshData()
})

// 设置默认日期范围
const setDefaultDateRange = () => {
  const endDate = dayjs()
  const startDate = dayjs().subtract(30, 'day')
  
  // 使用 dayjs 格式设置日期范围
  recordFilter.dateRange = [startDate, endDate]
}

// 重置日期范围
const resetDateRange = () => {
  recordDateRangeCustomized.value = false
  setDefaultDateRange()
  searchRecords()
}
</script>

<style scoped lang="less">
.usage-management {
  padding: 24px;
}

.page-header {
  margin-bottom: 24px;
  
  .header-content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 24px;
    
    h2 {
      margin: 0;
      font-size: 20px;
      font-weight: 600;
      flex-shrink: 0;
    }
    
    .header-actions {
      flex-shrink: 0;
    }
  }
}

.stats-row {
  margin-bottom: 24px;
}

.mb-4 {
  margin-bottom: 24px;
}

.mb-3 {
  margin-bottom: 12px;
}

.text-gray-500 {
  color: #6b7280;
}

.text-sm {
  font-size: 14px;
}

.mt-1 {
  margin-top: 4px;
}

/* 新增：预警面板样式 */
.alert-card {
  border-left: 4px solid #ff4d4f;
}

.alert-item {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
  padding: 8px;
  background-color: #fff2f0;
  border-radius: 4px;
}

.alert-text {
  color: #cf1322;
  font-weight: 500;
}

.alert-threshold {
  color: #8c8c8c;
  font-size: 12px;
}

.more-alerts {
  text-align: center;
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 8px;
  padding: 4px;
  background-color: #f5f5f5;
  border-radius: 4px;
}

.negative-amount {
  color: #ff4d4f;
  font-weight: bold;
}
</style>